/**
 * The checks that run when a document is generated (invented specifics, and
 * names that may not be in the data dictionary), turned into what the screens
 * show. Pure: no React, no Firestore.
 *
 *  - generationCheckRows: one Verification page guidance row per finding.
 *  - generationCheckBadge: the small read-only badge on a Study Documents card.
 *
 * The generators store the outcomes on the document itself, in `inventionCheck`
 * and `fieldNameCheck` (the Data Review and Cleaning Plan keeps both in
 * `verification.checks`). Nothing here uses an internal check id on screen.
 */
import type { GuidanceRow } from '../corrections/correctionRows';

/** The documents that store these checks, named as the Study Documents page names them. */
export const GENERATION_CHECK_DOCS: ReadonlyArray<{ docId: string; name: string; cardId?: string; /** How a sentence starts: "The ..." */ subject?: string }> = [
  { docId: 'dmp', name: 'Data Management Plan', cardId: 'dataManagementPlan' },
  { docId: 'editCheckSpec', name: 'Edit Checks', cardId: 'editChecks', subject: 'The edit check specification' },
  { docId: 'edts', name: 'External Data Transfer Specification', cardId: 'edts' },
  { docId: 'medicalCodingSpec', name: 'Medical Coding Specification', cardId: 'medicalCodingSpec' },
  { docId: 'drcp', name: 'Data Review and Cleaning Plan', cardId: 'drcp' },
  { docId: 'saerp', name: 'SAE Reconciliation Procedure', cardId: 'saerp' },
  { docId: 'dblc', name: 'Database Lock Checklist', cardId: 'dblc' },
  // Shown on the Study Build page as "UAT Protocol" and "UAT Report".
  { docId: 'uatProtocol', name: 'UAT Protocol' },
  { docId: 'uatReport', name: 'UAT Report' },
];

interface StoredItem {
  subject?: string;
  fingerprint?: string;
  detail?: string;
  kind?: string;
}
interface StoredOutcome {
  checkId?: string;
  status?: string;
  detail?: string;
  counts?: { findings?: number };
  items?: StoredItem[];
}
export type StoredGenerationDoc = {
  inventionCheck?: StoredOutcome | null;
  fieldNameCheck?: StoredOutcome | null;
  verification?: { checks?: StoredOutcome[] } | null;
} | null | undefined;

// Internal ids: used for the stored outcome and the dismissal key, never shown.
const INVENTION_ID = 'INV1';
const FIELD_NAME_ID = 'INV2';

function outcomes(doc: StoredGenerationDoc): { invention: StoredOutcome | null; fieldName: StoredOutcome | null } {
  if (!doc) return { invention: null, fieldName: null };
  const fromList = (id: string) =>
    (Array.isArray(doc.verification?.checks) ? doc.verification!.checks! : []).find((c) => c?.checkId === id) ?? null;
  return {
    invention: doc.inventionCheck ?? fromList(INVENTION_ID),
    fieldName: doc.fieldNameCheck ?? fromList(FIELD_NAME_ID),
  };
}

/** The first double-quoted piece of an item's detail: the specific itself. */
function quotedValue(detail: string | undefined): string | null {
  const m = /"([^"]+)"/.exec(detail ?? '');
  return m ? m[1] : null;
}

const KIND_PHRASE: Record<string, string> = {
  dictionary_version: 'names a dictionary version the study did not supply',
  vendor_name: 'names a vendor or product the study did not declare',
  count: "states a number the study's data does not support",
  protocol_number: 'gives a protocol number the study did not supply',
};
const FIELD_PHRASE = 'names a field that may not be in the data dictionary';

function line(lead: string, phrase: string, value: string | null, tail: string): string {
  return `${lead} ${phrase}${value ? `: "${value}"` : ''}. ${tail}`;
}

/** Field-name items are shown only at "likely" confidence (decision 2026-10-11). */
function isShownFieldItem(it: StoredItem & { data?: { confidence?: string } }): boolean {
  const c = it.data?.confidence ?? /^(\w+):/.exec(it.detail ?? '')?.[1];
  return c === 'likely';
}

/**
 * One guidance row per distinct finding stored on this document. SKIPPED and
 * PASS give none. Items repeated with the same section and fingerprint make one row.
 */
export function generationCheckRows(docId: string, doc: StoredGenerationDoc): GuidanceRow[] {
  const meta = GENERATION_CHECK_DOCS.find((d) => d.docId === docId);
  const lead = meta?.subject ?? `The ${meta?.name ?? docId}`;
  const { invention, fieldName } = outcomes(doc);
  const rows: GuidanceRow[] = [];
  const seen = new Set<string>();
  const add = (checkId: string, o: StoredOutcome | null, keep: (it: StoredItem) => boolean, textFor: (it: StoredItem) => string) => {
    if (!o || o.status !== 'FINDINGS') return;
    for (const it of Array.isArray(o.items) ? o.items : []) {
      if (!keep(it)) continue;
      const fingerprint = it.fingerprint ?? '';
      // Document + section + fingerprint: a dismissal holds until this text changes.
      const subject = `${docId}:${it.subject ?? ''}:${fingerprint}`;
      if (seen.has(`${checkId}:${subject}`)) continue;
      seen.add(`${checkId}:${subject}`);
      rows.push({
        kind: 'generation-finding',
        subject,
        checkId,
        ...(fingerprint ? { fingerprint } : {}),
        data: { doc: docId, line: textFor(it) },
      });
    }
  };
  add(INVENTION_ID, invention, () => true, (it) =>
    line(lead, KIND_PHRASE[it.kind ?? ''] ?? 'states a specific the study did not supply', quotedValue(it.detail), 'Edit or regenerate the document.'),
  );
  add(FIELD_NAME_ID, fieldName, isShownFieldItem, (it) =>
    line(lead, FIELD_PHRASE, quotedValue(it.detail), 'Check it against the data dictionary, then edit or regenerate the document.'),
  );
  return rows;
}

export interface DismissalLike { key: string; fingerprint: string }

/** Same rule as the Verification page: key = checkId:subject, re-shown when the fingerprint changes. */
export function isRowDismissed(row: GuidanceRow, dismissals: ReadonlyArray<DismissalLike>): boolean {
  if (!row.checkId) return false;
  return dismissals.some((d) => d.key === `${row.checkId}:${row.subject}` && d.fingerprint === (row.fingerprint ?? ''));
}

export interface GenerationCheckBadge {
  state: 'checked' | 'findings' | 'dismissed' | 'notChecked';
  label: string;
  title: string;
  count: number;
}

/**
 * Null when the document stores neither check (generated before they existed).
 * Counts OPEN findings, the ones the Verification page shows: repeated items
 * once, low-confidence field names left out, dismissed ones subtracted.
 */
export function generationCheckBadge(
  doc: StoredGenerationDoc,
  opts: { docId?: string; dismissals?: ReadonlyArray<DismissalLike> } = {},
): GenerationCheckBadge | null {
  const { invention, fieldName } = outcomes(doc);
  const present = [invention, fieldName].filter((o): o is StoredOutcome => !!o && typeof o.status === 'string');
  if (present.length === 0) return null;
  const all = generationCheckRows(opts.docId ?? '', doc);
  const open = all.filter((r) => !isRowDismissed(r, opts.dismissals ?? []));
  if (open.length > 0) {
    const count = open.length;
    return {
      state: 'findings',
      label: `${count} finding${count === 1 ? '' : 's'}`,
      title: 'The check run when this document was generated noted findings. Open the Verification page to see them.',
      count,
    };
  }
  if (all.length > 0) {
    return {
      state: 'dismissed',
      label: `${all.length} dismissed`,
      title: 'Every finding from the check run when this document was generated has been dismissed on the Verification page.',
      count: 0,
    };
  }
  // Not checked only when no check ran to the end: every stored check was skipped.
  if (!present.some((o) => o.status === 'PASS' || o.status === 'FINDINGS')) {
    return {
      state: 'notChecked',
      label: 'Not checked',
      title: 'The check run when this document was generated could not run.',
      count: 0,
    };
  }
  return {
    state: 'checked',
    label: 'Checked ✓',
    title: 'The check run when this document was generated found nothing to report.',
    count: 0,
  };
}
